import React, { useEffect, useId } from 'react';

import FreeformOption from './freeform-option';
import GuidedPromptOption from './guided-prompt-option';
import GuidedPromptHeader from './guided-prompt-header';
import GuidedPromptFooter from './guided-prompt-footer';

import useGuidedPromptForm from 'containers/admin/clients/client/tabs-components/ai-assistant/hooks/use-guided-prompt-form';

import type {
  GuidedPrompt,
  GuidedPromptAnswers,
} from 'containers/admin/clients/client/tabs-components/ai-assistant/helpers/guided-prompt-helpers';

interface GuidedPromptFormProps {
  prompts: GuidedPrompt[];
  onSubmit: (answers: GuidedPromptAnswers) => void;
  onDismiss?: () => void;
}

const GuidedPromptForm = ({
  prompts,
  onSubmit,
  onDismiss,
}: GuidedPromptFormProps): React.JSX.Element => {
  const questionId = useId();

  const {
    currentStep,
    currentPrompt,
    totalSteps,
    isMultiStep,
    isLastStep,
    isMultiSelect,
    currentAnswer,
    canProceed,
    isFreeformActive,
    handleOptionSelect,
    handleFreeformChange,
    handleNext,
    handlePrevious,
    handleSkip,
  } = useGuidedPromptForm({ prompts, onSubmit });

  useEffect(() => {
    if (!onDismiss) return undefined;

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      onDismiss();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onDismiss]);

  return (
    <form
      aria-label="Guided prompt"
      onSubmit={(e) => {
        e.preventDefault();
        handleNext();
      }}
      className=" rounded-lg border border-navy-800 bg-white shadow-sm"
    >
      <GuidedPromptHeader
        questionId={questionId}
        promptText={currentPrompt.text}
        isMultiStep={isMultiStep}
        currentStep={currentStep}
        totalSteps={totalSteps}
        isMultiSelect={isMultiSelect}
        isRequired={currentPrompt.required === true}
        onDismiss={onDismiss}
      />

      <ul
        role={isMultiSelect ? 'group' : 'radiogroup'}
        aria-labelledby={questionId}
        className="list-none p-3 px-4 flex flex-col gap-1.5 max-h-[260px] overflow-y-auto scrollbar-thin"
      >
        {currentPrompt.options?.map((option) => (
          <li key={option.label}>
            <GuidedPromptOption
              label={option.label}
              questionType={currentPrompt.questionType}
              isSelected={currentAnswer.selectedOptions.includes(option.label)}
              onSelect={handleOptionSelect}
            />
          </li>
        ))}

        <li>
          <FreeformOption
            value={currentAnswer.freeformText}
            isActive={isFreeformActive}
            onChange={handleFreeformChange}
          />
        </li>
      </ul>

      <GuidedPromptFooter
        isMultiStep={isMultiStep}
        currentStep={currentStep}
        isLastStep={isLastStep}
        isRequired={currentPrompt.required === true}
        canProceed={canProceed}
        onPrevious={handlePrevious}
        onSkip={handleSkip}
      />
    </form>
  );
};

export default GuidedPromptForm;
